import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
	deleteBulkPaymentApi,
	generateBulkPaymentApi,
	getBulkPaymentApi,
	getBulkPaymentFileApi,
	listBulkPaymentsApi,
} from "./bulkPaymentsAPI";

const initialState = {
	batches: [],
	selectedBatch: null,
	status: "idle",
	detailStatus: "idle",
	mutationStatus: "idle",
	error: null,
};

export const listBulkPaymentsAsync = createAsyncThunk("bulkPayments/list", async (_, { rejectWithValue }) => {
	try {
		return await listBulkPaymentsApi();
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const getBulkPaymentAsync = createAsyncThunk("bulkPayments/get", async (batchId, { rejectWithValue }) => {
	try {
		return await getBulkPaymentApi(batchId);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const getBulkPaymentFileAsync = createAsyncThunk("bulkPayments/file", async (batchId, { rejectWithValue }) => {
	try {
		return await getBulkPaymentFileApi(batchId);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const deleteBulkPaymentAsync = createAsyncThunk("bulkPayments/delete", async (batchId, { rejectWithValue }) => {
	try {
		return await deleteBulkPaymentApi(batchId);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const generateBulkPaymentAsync = createAsyncThunk("bulkPayments/generate", async (request, { rejectWithValue }) => {
	try {
		return await generateBulkPaymentApi(request);
	} catch (error) {
		return rejectWithValue(error);
	}
});

const bulkPaymentsSlice = createSlice({
	name: "bulkPayments",
	initialState,
	reducers: {
		clearBulkPaymentsError(state) {
			state.error = null;
		},
		clearSelectedBatch(state) {
			state.selectedBatch = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(listBulkPaymentsAsync.pending, (state) => {
				state.status = "loading";
				state.error = null;
			})
			.addCase(listBulkPaymentsAsync.fulfilled, (state, action) => {
				state.status = "idle";
				state.batches = action.payload?.batches || [];
			})
			.addCase(listBulkPaymentsAsync.rejected, (state, action) => {
				state.status = "idle";
				state.error = action.payload?.message || "Unexpected error";
			})
			.addCase(getBulkPaymentAsync.pending, (state) => {
				state.detailStatus = "loading";
			})
			.addCase(getBulkPaymentAsync.fulfilled, (state, action) => {
				state.detailStatus = "idle";
				state.selectedBatch = action.payload?.batch || null;
			})
			.addCase(getBulkPaymentAsync.rejected, (state, action) => {
				state.detailStatus = "idle";
				state.error = action.payload?.message || "Unexpected error";
			})
			.addCase(generateBulkPaymentAsync.pending, (state) => {
				state.mutationStatus = "loading";
				state.error = null;
			})
			.addCase(generateBulkPaymentAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				const batch = action.payload?.batch;
				if (batch) {
					state.batches = [batch, ...state.batches.filter((item) => item.batchId !== batch.batchId)];
				}
			})
			.addCase(generateBulkPaymentAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload?.message || "Unexpected error";
			})
			.addCase(deleteBulkPaymentAsync.pending, (state) => {
				state.mutationStatus = "loading";
			})
			.addCase(deleteBulkPaymentAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				const batchId = action.meta.arg;
				state.batches = state.batches.filter((batch) => batch.batchId !== batchId);
				if (state.selectedBatch?.batchId === batchId) state.selectedBatch = null;
			})
			.addCase(deleteBulkPaymentAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload?.message || "Unexpected error";
			})
			.addCase(getBulkPaymentFileAsync.rejected, (state, action) => {
				state.error = action.payload?.message || "Unexpected error";
			});
	},
});

export const { clearBulkPaymentsError, clearSelectedBatch } = bulkPaymentsSlice.actions;
export const selectBulkPaymentBatches = (state) => state.bulkPayments.batches;
export default bulkPaymentsSlice.reducer;
